import React, { useCallback, useEffect, useMemo, useState } from "react";
import { MaterialSearchPicker } from "./MaterialSearchPicker";
import {
  buildMaterialDraftFromCatalog,
  flushSessionMaterialSync,
  getSessionMaterialSyncSummary,
  getSessionMaterials,
  isSessionMaterialLineCommitted,
  persistSessionMaterialLineEdit,
  persistSessionMaterialLineRemoval,
  SESSION_MATERIAL_SYNC_STATUS,
  syncUnsyncedSessionMaterialLines,
  upsertSessionMaterialLine,
} from "../helpers/sessionMaterials";
import { loadMaterialsCatalog } from "../helpers/materialsCatalog";
import { getMaterialRelationName } from "../helpers/materialVisibility";
import { usePermissions } from "../hooks/usePermissions";
import { CAPABILITY } from "../helpers/permissions";
import {
  findMatchingQuoteLine,
  loadProjectQuoteLinesCached,
} from "../helpers/projectQuoteLineMatching";
import { QUOTE_LINE_TYPE } from "../helpers/projectQuoteSnapshots";
import { CPInput } from "./CPInput";
import { safeUserErrorMessage } from "../helpers/safeErrors";

export function SessionMaterialsPanel({
  sessionId,
  projectId,
  mode = "capture",
  usageDate,
  initialOpenAddForm = false,
  compact = false,
}) {
  const { actor, orgPolicies, can } = usePermissions();
  const readOnly = mode === "readonly";
  // Voiding a committed ledger row is a manager+ capability (the ledger trigger
  // rejects anyone else). Local, never-committed drafts can always be removed.
  const canVoidCommitted = can(CAPABILITY.MATERIAL_USAGE_VOID);
  const canRemoveLine = (line) =>
    !isSessionMaterialLineCommitted(line) || canVoidCommitted;
  const [lines, setLines] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [loadingCatalog, setLoadingCatalog] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [addMaterialId, setAddMaterialId] = useState("");
  const [addQuantity, setAddQuantity] = useState("");
  const [addNotes, setAddNotes] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editQuantity, setEditQuantity] = useState("");
  const [editNotes, setEditNotes] = useState("");
  const [materialQuoteLines, setMaterialQuoteLines] = useState([]);
  const [actionError, setActionError] = useState("");

  const selectedMaterial = useMemo(
    () => materials.find((item) => item.material_id === addMaterialId),
    [materials, addMaterialId],
  );

  const syncSummary = useMemo(
    () => getSessionMaterialSyncSummary(lines),
    [lines],
  );

  const refreshLines = useCallback(() => {
    if (!sessionId) {
      setLines([]);
      return [];
    }
    const latest = getSessionMaterials(sessionId);
    setLines(latest);
    return latest;
  }, [sessionId]);

  const scheduleSync = useCallback(() => {
    if (readOnly || !sessionId || !projectId || !actor?.supabase_user_id) {
      return;
    }

    void syncUnsyncedSessionMaterialLines({
      sessionId,
      projectId,
      enteredBy: actor.supabase_user_id,
      usageDate,
    }).finally(() => {
      refreshLines();
    });
  }, [sessionId, projectId, actor, usageDate, readOnly, refreshLines]);

  const loadCatalog = useCallback(async () => {
    setLoadingCatalog(true);

    const relation = getMaterialRelationName(actor, orgPolicies);
    const { materials: catalog } = await loadMaterialsCatalog({
      onCached: (cached) => {
        setMaterials(cached);
        setLoadingCatalog(false);
      },
      relation,
      actor,
    });

    setMaterials(catalog);
    setLoadingCatalog(false);
  }, [actor, orgPolicies]);

  useEffect(() => {
    loadCatalog();
  }, [loadCatalog]);

  useEffect(() => {
    if (!projectId) {
      setMaterialQuoteLines([]);
      return;
    }

    let cancelled = false;

    loadProjectQuoteLinesCached(projectId)
      .then((quoteLines) => {
        if (cancelled) return;
        setMaterialQuoteLines(
          (quoteLines || []).filter(
            (line) => line.line_type === QUOTE_LINE_TYPE.MATERIAL,
          ),
        );
      })
      .catch(() => {
        if (cancelled) return;
        setMaterialQuoteLines([]);
      });

    return () => {
      cancelled = true;
    };
  }, [projectId]);

  function getQuoteHint(materialId) {
    if (!materialId || !materialQuoteLines.length) return null;

    const match = findMatchingQuoteLine(materialQuoteLines, {
      lineType: QUOTE_LINE_TYPE.MATERIAL,
      materialId,
    });

    if (!match) return null;

    return `Quoted: ${match.description_snapshot}`;
  }

  useEffect(() => {
    if (!sessionId) {
      setLines([]);
      return;
    }

    refreshLines();
    setShowAddForm(Boolean(initialOpenAddForm));
    setEditingId(null);
  }, [sessionId, initialOpenAddForm, refreshLines]);

  function resetAddForm() {
    setAddMaterialId("");
    setAddQuantity("");
    setAddNotes("");
    setShowAddForm(false);
  }

  function handleAddMaterial() {
    if (!sessionId) return;
    setActionError("");

    if (!addMaterialId) {
      alert("Select a material.");
      return;
    }

    const quantity = Number(addQuantity);
    if (!quantity || quantity <= 0) {
      alert("Enter a quantity greater than zero.");
      return;
    }

    if (!selectedMaterial) {
      alert("Selected material not found.");
      return;
    }

    const draft = buildMaterialDraftFromCatalog(
      selectedMaterial,
      quantity,
      addNotes,
      usageDate,
    );

    const next = upsertSessionMaterialLine(sessionId, draft);
    setLines(next);
    resetAddForm();
    scheduleSync();
  }

  async function handleRetrySync() {
    setActionError("");
    await flushSessionMaterialSync(sessionId);
    scheduleSync();
  }

  async function handleDeleteLine(localId) {
    setActionError("");
    try {
      const next = await persistSessionMaterialLineRemoval({
        sessionId,
        localId,
        voidedBy: actor?.supabase_user_id || null,
        actor,
      });
      setLines(next);
    } catch (error) {
      setActionError(
        safeUserErrorMessage(error, "Materials could not be saved — Retry"),
      );
      refreshLines();
    }
  }

  function startEditLine(line) {
    setEditingId(line.localId);
    setEditQuantity(String(line.quantity));
    setEditNotes(line.notes || "");
  }

  async function handleSaveEdit(localId) {
    const quantity = Number(editQuantity);
    if (!quantity || quantity <= 0) {
      alert("Enter a quantity greater than zero.");
      return;
    }

    setActionError("");
    try {
      const next = await persistSessionMaterialLineEdit({
        sessionId,
        localId,
        quantity,
        notes: editNotes.trim(),
        enteredBy: actor?.supabase_user_id || null,
      });
      setLines(next);
      setEditingId(null);
      scheduleSync();
    } catch (error) {
      setActionError(
        safeUserErrorMessage(error, "Materials could not be saved — Retry"),
      );
      refreshLines();
    }
  }

  const sectionTitle =
    mode === "readonly"
      ? "Materials Used"
      : mode === "review"
        ? "Materials Review"
        : "Materials";

  const materialsIcon = (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3.5 20 8v8l-8 4.5L4 16V8Z" />
      <path d="M4 8l8 4.5L20 8M12 12.5V21" />
    </svg>
  );

  if (compact && !readOnly && !showAddForm && lines.length === 0) {
    return (
      <div className="sw-icon-row sw-materials-compact-row">
        <span className="sw-icon-row-icon" aria-hidden="true">
          {materialsIcon}
        </span>
        <div className="sw-icon-row-body">
          <div className="sw-icon-row-label">Materials</div>
          <div className="sw-materials-compact-empty">No materials added</div>
        </div>
        <button
          type="button"
          className="nx-btn sw-btn-secondary sw-btn-add-material-compact"
          onClick={() => setShowAddForm(true)}
          disabled={!sessionId}
        >
          + Add
        </button>
      </div>
    );
  }

  if (compact && !readOnly) {
    return (
      <div className="sw-materials-compact-panel">
        <div className="sw-icon-row sw-materials-compact-header">
          <span className="sw-icon-row-icon" aria-hidden="true">
            {materialsIcon}
          </span>
          <div className="sw-icon-row-body">
            <div className="sw-icon-row-label">Materials</div>
            {lines.length > 0 && (
              <div className="sw-icon-row-value">
                {lines.length} item{lines.length !== 1 ? "s" : ""}
              </div>
            )}
          </div>
        </div>

        {lines.length > 0 && (
          <div className="sw-materials-compact-list">
            {lines.map((line) => {
              const isEditing = editingId === line.localId;

              return (
                <div key={line.localId} className="sw-material-compact-row">
                  {isEditing ? (
                    <div className="sw-material-compact-edit-form">
                      <div className="sw-material-compact-name">
                        {line.material_name_snapshot}
                      </div>
                      <CPInput
                        className="cp-input-group--compact"
                        label="Quantity"
                        type="number"
                        min="0"
                        step="any"
                        value={editQuantity}
                        onChange={setEditQuantity}
                        placeholder="0"
                      />
                      <CPInput
                        className="cp-input-group--compact"
                        label="Notes"
                        value={editNotes}
                        onChange={setEditNotes}
                        placeholder="Optional"
                      />
                      <div className="sw-inline-actions">
                        <button
                          type="button"
                          className="nx-btn sw-btn-secondary"
                          onClick={() => setEditingId(null)}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="nx-btn nx-btn-gold"
                          onClick={() => handleSaveEdit(line.localId)}
                        >
                          Save
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="sw-material-compact-main">
                        <span
                          className="sw-material-compact-name"
                          title={line.material_name_snapshot}
                        >
                          {line.material_name_snapshot}
                        </span>
                        <div className="sw-material-compact-actions">
                          <button
                            type="button"
                            className="sw-material-compact-edit"
                            onClick={() => startEditLine(line)}
                          >
                            Edit
                          </button>
                          {canRemoveLine(line) ? (
                            <button
                              type="button"
                              className="sw-material-compact-remove"
                              onClick={() => handleDeleteLine(line.localId)}
                              aria-label={`Remove ${line.material_name_snapshot}`}
                            >
                              ×
                            </button>
                          ) : null}
                        </div>
                      </div>
                      <div className="sw-material-compact-meta">
                        {Number(line.quantity)}{" "}
                        {line.unit_of_measure_snapshot || "units"}
                        {line.sku_snapshot ? ` · SKU ${line.sku_snapshot}` : ""}
                      </div>
                      {line.notes ? (
                        <div className="sw-material-compact-meta">
                          {line.notes}
                        </div>
                      ) : null}
                      {getQuoteHint(line.material_id) && (
                        <div
                          className="sw-material-compact-meta"
                          style={{ color: "#c9a84c" }}
                        >
                          {getQuoteHint(line.material_id)}
                        </div>
                      )}
                      {line.sync_status === SESSION_MATERIAL_SYNC_STATUS.ERROR ? (
                        <div className="sw-material-compact-meta">
                          Could not save — retry
                        </div>
                      ) : null}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {syncSummary.label ? (
          <p
            className="ci-review-row-empty sw-materials-compact-status"
            data-testid="session-materials-sync-status"
          >
            {syncSummary.label}
          </p>
        ) : null}

        {syncSummary.kind === "error" ? (
          <button
            type="button"
            className="nx-btn sw-btn-secondary sw-btn-full"
            onClick={handleRetrySync}
          >
            Retry
          </button>
        ) : null}

        {actionError ? (
          <p
            className="ci-review-row-empty sw-materials-compact-status"
            style={{ color: "#e06b5a" }}
          >
            {actionError}
          </p>
        ) : null}

        {sessionId && (
          <>
            {showAddForm ? (
              <div className="sw-materials-add-form">
                {loadingCatalog ? (
                  <p className="ci-review-row-empty">Loading materials…</p>
                ) : (
                  <>
                    <MaterialSearchPicker
                      materials={materials}
                      value={addMaterialId}
                      onChange={setAddMaterialId}
                      disabled={loadingCatalog}
                      showPricing={false}
                    />
                    {getQuoteHint(addMaterialId) && (
                      <p
                        className="ci-review-row-empty"
                        style={{ color: "#c9a84c", marginTop: "8px" }}
                      >
                        {getQuoteHint(addMaterialId)}
                      </p>
                    )}

                    <CPInput
                      className="cp-input-group--compact"
                      id="session-material-quantity"
                      label="Quantity"
                      type="number"
                      min="0"
                      step="any"
                      value={addQuantity}
                      onChange={setAddQuantity}
                      placeholder="0"
                    />

                    <CPInput
                      className="cp-input-group--compact"
                      id="session-material-notes"
                      label="Notes"
                      value={addNotes}
                      onChange={setAddNotes}
                      placeholder="Optional"
                    />

                    <div className="sw-inline-actions">
                      <button
                        type="button"
                        className="nx-btn sw-btn-secondary"
                        onClick={resetAddForm}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="nx-btn nx-btn-gold"
                        onClick={handleAddMaterial}
                      >
                        + Add Material
                      </button>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <button
                type="button"
                className="nx-btn sw-btn-secondary sw-btn-add-material-list"
                onClick={() => setShowAddForm(true)}
                disabled={!sessionId}
              >
                + Add Material
              </button>
            )}
          </>
        )}
      </div>
    );
  }

  return (
    <div className="kpi-chart-card sw-section">
      <div className="kpi-section-label">{sectionTitle}</div>

      <div className="kpi-card">
        {lines.length === 0 ? (
          <p className="ci-review-row-empty sw-materials-empty">
            {readOnly
              ? "No materials recorded for this session."
              : "No materials added"}
          </p>
        ) : (
          <div className={readOnly ? "" : "sw-materials-list"}>
            {lines.map((line) => {
              const isEditing = editingId === line.localId;

              return (
                <div key={line.localId} className="kpi-card sw-material-row">
                  <div className="sw-material-row-header">
                    <div className="kpi-card-value sw-material-name">
                      {line.material_name_snapshot}
                    </div>
                  </div>

                  {isEditing && !readOnly ? (
                    <>
                      <input
                        type="number"
                        min="0"
                        step="any"
                        value={editQuantity}
                        onChange={(e) => setEditQuantity(e.target.value)}
                        placeholder="Quantity"
                        className="sw-field-input"
                      />
                      <input
                        type="text"
                        value={editNotes}
                        onChange={(e) => setEditNotes(e.target.value)}
                        placeholder="Notes (optional)"
                        className="sw-field-input"
                      />
                      <div className="sw-inline-actions">
                        <button
                          type="button"
                          className="nx-btn nx-btn-gold"
                          onClick={() => handleSaveEdit(line.localId)}
                        >
                          Save
                        </button>
                        <button
                          type="button"
                          className="nx-btn sw-btn-secondary"
                          onClick={() => setEditingId(null)}
                        >
                          Cancel
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="ci-review-row-value sw-material-detail">
                        {Number(line.quantity)}{" "}
                        {line.unit_of_measure_snapshot || "units"}
                        {line.sku_snapshot ? ` · SKU ${line.sku_snapshot}` : ""}
                      </div>
                      {line.notes ? (
                        <div className="ci-review-row-empty sw-material-notes">
                          {line.notes}
                        </div>
                      ) : null}
                      {getQuoteHint(line.material_id) && (
                        <div
                          className="ci-review-row-empty sw-material-notes"
                          style={{ color: "#c9a84c" }}
                        >
                          {getQuoteHint(line.material_id)}
                        </div>
                      )}
                      {line.sync_status === SESSION_MATERIAL_SYNC_STATUS.ERROR ? (
                        <div className="ci-review-row-empty sw-material-notes">
                          Could not save — retry
                        </div>
                      ) : null}

                      {!readOnly && (
                        <div className="sw-material-row-actions">
                          <button
                            type="button"
                            className="nx-btn sw-btn-secondary"
                            onClick={() => startEditLine(line)}
                          >
                            Edit
                          </button>
                          {canRemoveLine(line) ? (
                            <button
                              type="button"
                              className="nx-btn sw-btn-secondary sw-btn-remove"
                              onClick={() => handleDeleteLine(line.localId)}
                            >
                              Remove
                            </button>
                          ) : null}
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {!readOnly && syncSummary.label ? (
          <p className="ci-review-row-empty" data-testid="session-materials-sync-status">
            {syncSummary.label}
          </p>
        ) : null}

        {!readOnly && syncSummary.kind === "error" ? (
          <button
            type="button"
            className="nx-btn sw-btn-secondary sw-btn-full"
            onClick={handleRetrySync}
          >
            Retry
          </button>
        ) : null}

        {actionError ? (
          <p className="ci-review-row-empty" style={{ color: "#e06b5a" }}>
            {actionError}
          </p>
        ) : null}

        {!readOnly && sessionId && (
          <>
            {showAddForm ? (
              <div className="sw-materials-add-form">
                {loadingCatalog ? (
                  <p className="ci-review-row-empty">Loading materials…</p>
                ) : (
                  <>
                    <MaterialSearchPicker
                      materials={materials}
                      value={addMaterialId}
                      onChange={setAddMaterialId}
                      disabled={loadingCatalog}
                      showPricing={false}
                    />
                    {getQuoteHint(addMaterialId) && (
                      <p
                        className="ci-review-row-empty"
                        style={{ color: "#c9a84c", marginTop: "8px" }}
                      >
                        {getQuoteHint(addMaterialId)}
                      </p>
                    )}

                    <CPInput
                      className="cp-input-group--compact"
                      id="session-material-quantity"
                      label="Quantity"
                      type="number"
                      min="0"
                      step="any"
                      value={addQuantity}
                      onChange={setAddQuantity}
                      placeholder="0"
                    />

                    <CPInput
                      className="cp-input-group--compact"
                      id="session-material-notes"
                      label="Notes"
                      value={addNotes}
                      onChange={setAddNotes}
                      placeholder="Optional"
                    />

                    <div className="sw-inline-actions">
                      <button
                        type="button"
                        className="nx-btn nx-btn-gold"
                        onClick={handleAddMaterial}
                      >
                        Add
                      </button>
                      <button
                        type="button"
                        className="nx-btn sw-btn-secondary"
                        onClick={resetAddForm}
                      >
                        Cancel
                      </button>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <button
                type="button"
                className="nx-btn sw-btn-secondary sw-btn-full"
                onClick={() => setShowAddForm(true)}
                disabled={!sessionId}
              >
                + Add Material
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
